import React, { useState } from "react";
import { supabase } from "./supabase/auth";
import {
  Ingredient,
  saveIngredient,
  PROTEIN_CLASSES,
  PROTEIN_CLASS_LABELS,
  toProteinClass,
} from "./helpers/saveIngredient";
import { useTranslation } from 'react-i18next';
import ColorPickerField from "./ColorPickerField";

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const sectionBox: React.CSSProperties = {
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  marginBottom: "16px",
};

const sectionTitle: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  background: "#f0f0f0",
  borderBottom: "1px solid #d0d0d0",
  color: "#000000",
};

const fieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #d0d0d0",
  fontSize: "12px",
};

const fieldLabelStyle: React.CSSProperties = {
  width: "180px",
  minWidth: "180px",
  padding: "4px 10px",
  background: "#f8f8f8",
  fontWeight: 600,
  color: "#333333",
  borderRight: "1px solid #d0d0d0",
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: "3px 8px",
  fontSize: "12px",
  fontFamily: mono,
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  color: "#000000",
  outline: "none",
  margin: "2px 4px",
};

const btnStyle: React.CSSProperties = {
  padding: "4px 14px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #d0d0d0",
  background: "#f0f0f0",
  cursor: "pointer",
  color: "#000000",
};

const errorStyle: React.CSSProperties = { color: "#cc0000", fontSize: "11px", marginLeft: "8px" };
const warnStyle: React.CSSProperties = { color: "#996600", fontSize: "11px", background: "#fff9db", padding: "1px 4px", marginLeft: "8px" };

const PASTE_ORDER = [
  "id", "common_name", "korean_name", "fda_name", "color", "supplier", "cost_per_kg",
  "protein_per_100g", "digestibility", "fat", "satfat", "transfat", "cholesterol",
  "carbs", "fiber", "sugar", "sodium", "moisture", "ash",
  "aa_his", "aa_ile", "aa_leu", "aa_lys", "aa_mc", "aa_pt", "aa_thr", "aa_trp", "aa_val",
  "vitamin_d", "calcium", "iron", "potassium",
  "sub_ingredients", "notes_spec_ref", "tags",
  "fa_ala", "fa_la", "fa_oleic", "fa_palmitic", "fa_stearic",
  "fiber_insoluble", "fiber_soluble", "resistant_starch",
  "ref_digest_product", "ref_digest_value", "ref_digest_source",
];

const TEXT_FIELDS = new Set(["id", "common_name", "korean_name", "fda_name", "color", "supplier", "sub_ingredients", "notes_spec_ref", "data_source_default", "ref_digest_product", "ref_digest_source"]);

interface FormState {
  id: string;
  common_name: string;
  korean_name: string;
  fda_name: string;
  supplier: string;
  color: string;
  tags: string[];
  /** "" means unset, saved as null. */
  protein_class: string;
  protein_per_100g: string;
  digestibility: string;
  fat: string;
  satfat: string;
  transfat: string;
  cholesterol: string;
  carbs: string;
  fiber: string;
  sugar: string;
  sodium: string;
  moisture: string;
  ash: string;
  cost_per_kg: string;
  aa_his: string;
  aa_ile: string;
  aa_leu: string;
  aa_lys: string;
  aa_mc: string;
  aa_pt: string;
  aa_thr: string;
  aa_trp: string;
  aa_val: string;
  vitamin_d: string;
  calcium: string;
  iron: string;
  potassium: string;
  sub_ingredients: string;
  notes_spec_ref: string;
  // Fatty acids
  fa_ala: string;
  fa_la: string;
  fa_oleic: string;
  fa_palmitic: string;
  fa_stearic: string;
  // Fiber fractions
  fiber_insoluble: string;
  fiber_soluble: string;
  resistant_starch: string;
  // Reference digestibility
  ref_digest_product: string;
  ref_digest_value: string;
  ref_digest_source: string;
  // Data source
  data_source_default: string;
}

function emptyForm(): FormState {
  return {
    id: "", common_name: "", korean_name: "", fda_name: "", supplier: "", color: "",
    tags: [],
    protein_class: "",
    protein_per_100g: "", digestibility: "", fat: "", satfat: "", transfat: "",
    cholesterol: "", carbs: "", fiber: "", sugar: "", sodium: "", moisture: "", ash: "",
    cost_per_kg: "",
    aa_his: "", aa_ile: "", aa_leu: "", aa_lys: "", aa_mc: "", aa_pt: "",
    aa_thr: "", aa_trp: "", aa_val: "",
    vitamin_d: "", calcium: "", iron: "", potassium: "",
    sub_ingredients: "", notes_spec_ref: "",
    fa_ala: "", fa_la: "", fa_oleic: "", fa_palmitic: "", fa_stearic: "",
    fiber_insoluble: "", fiber_soluble: "", resistant_starch: "",
    ref_digest_product: "", ref_digest_value: "", ref_digest_source: "",
    data_source_default: "",
  };
}

function formToIngredient(f: FormState): Partial<Ingredient> {
  // Non-finite results (junk from paste, "1e999") become null, not NaN — NaN
  // passes every range check (all comparisons false) and saves as null or a 400.
  const num = (v: string) => {
    if (v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    id: f.id,
    common_name: f.common_name,
    korean_name: f.korean_name || null,
    fda_name: f.fda_name || null,
    supplier: f.supplier || null,
    color: f.color || null,
    tags: f.tags,
    // Blank stays null. The column is nullable and must never receive "".
    protein_class: toProteinClass(f.protein_class),
    protein_per_100g: num(f.protein_per_100g),
    digestibility: num(f.digestibility),
    fat: num(f.fat),
    satfat: num(f.satfat),
    transfat: num(f.transfat),
    cholesterol: num(f.cholesterol),
    carbs: num(f.carbs),
    fiber: num(f.fiber),
    sugar: num(f.sugar),
    sodium: num(f.sodium),
    moisture: num(f.moisture),
    ash: num(f.ash),
    cost_per_kg: num(f.cost_per_kg),
    aa_his: num(f.aa_his),
    aa_ile: num(f.aa_ile),
    aa_leu: num(f.aa_leu),
    aa_lys: num(f.aa_lys),
    aa_mc: num(f.aa_mc),
    aa_pt: num(f.aa_pt),
    aa_thr: num(f.aa_thr),
    aa_trp: num(f.aa_trp),
    aa_val: num(f.aa_val),
    vitamin_d: num(f.vitamin_d),
    calcium: num(f.calcium),
    iron: num(f.iron),
    potassium: num(f.potassium),
    sub_ingredients: f.sub_ingredients || null,
    notes_spec_ref: f.notes_spec_ref || null,
    fa_ala: num(f.fa_ala),
    fa_la: num(f.fa_la),
    fa_oleic: num(f.fa_oleic),
    fa_palmitic: num(f.fa_palmitic),
    fa_stearic: num(f.fa_stearic),
    fiber_insoluble: num(f.fiber_insoluble),
    fiber_soluble: num(f.fiber_soluble),
    resistant_starch: num(f.resistant_starch),
    bcaa_mg_per_g: (() => {
      const l = num(f.aa_leu), i = num(f.aa_ile), v = num(f.aa_val);
      return (l != null || i != null || v != null) ? (l ?? 0) + (i ?? 0) + (v ?? 0) : null;
    })(),
    ref_digest_product: f.ref_digest_product || null,
    ref_digest_value: num(f.ref_digest_value),
    ref_digest_source: f.ref_digest_source || null,
    data_sources: f.data_source_default.trim() ? { _default: f.data_source_default.trim() } : null,
  };
}

function TagInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState("");

  const addTag = (raw: string) => {
    const t = raw.trim();
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onChange([...tags, t]);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
      setInput("");
    }
    if (e.key === "Backspace" && input === "" && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px", flex: 1, padding: "2px 4px" }}>
      {tags.map((t, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "1px 6px", background: "#e8e8e8", border: "1px solid #ccc", fontSize: "11px", gap: "4px" }}>
          {t}
          <span style={{ cursor: "pointer", fontWeight: 700, color: "#666" }} onClick={() => onChange(tags.filter((_, j) => j !== i))}>x</span>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tags.length === 0 ? (placeholder || "") : ""}
        style={{ ...inputStyle, border: "none", margin: 0, minWidth: "100px", flex: 1 }}
      />
    </div>
  );
}

function TextInput({ label, value, onChange, readOnly, error, warning }: {
  label: string; value: string; onChange: (v: string) => void; readOnly?: boolean; error?: string; warning?: string;
}) {
  return (
    <div style={fieldRowStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} style={{ ...inputStyle, ...(readOnly ? { background: "#f0f0f0" } : {}) }} />
      {error && <span style={errorStyle}>{error}</span>}
      {warning && <span style={warnStyle}>{warning}</span>}
    </div>
  );
}

function NumInput({ label, value, onChange, step, error, warning }: {
  label: string; value: string; onChange: (v: string) => void; step?: string; error?: string; warning?: string;
}) {
  return (
    <div style={fieldRowStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <input type="number" step={step || "any"} value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
      {error && <span style={errorStyle}>{error}</span>}
      {warning && <span style={warnStyle}>{warning}</span>}
    </div>
  );
}

export default function IngredientCreateScreen({
  onBack,
  onSaved,
}: {
  onBack: () => void;
  onSaved: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ko" ? "ko" : "en";
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");

  const set = (field: keyof FormState) => (val: string) => setForm((f) => ({ ...f, [field]: val }));

  const handlePaste = (text: string) => {
    setPasteText(text);
    const cells = text.split("\t");
    const updated = { ...form };
    for (let i = 0; i < Math.min(cells.length, PASTE_ORDER.length); i++) {
      const key = PASTE_ORDER[i];
      const val = cells[i]?.trim() || "";
      if (key === "tags") {
        (updated as any).tags = val.split(",").map((s: string) => s.trim()).filter(Boolean);
      } else {
        (updated as any)[key] = val;
      }
    }
    setForm(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    setWarnings([]);

    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || "unknown";

    const result = await saveIngredient(formToIngredient(form), "create", null, email);
    setSaving(false);

    if (!result.success) {
      setErrors(result.errors);
      setWarnings(result.warnings);
      return;
    }
    if (result.warnings.length > 0) {
      // Partial failure (e.g. change-log write failed): keep the screen
      // mounted so the warning is seen; navigate only on explicit continue.
      setWarnings(result.warnings);
      setSavedId(result.data!.id);
      return;
    }
    onSaved(result.data!.id);
  };

  return (
    <div style={{ background: "#ffffff", padding: "8px", minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <button style={btnStyle} onClick={onBack}>{t('ingredientForm.cancel')}</button>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#000" }}>{t('ingredientForm.createIngredient')}</span>
        <button style={{ ...btnStyle, marginLeft: "auto", background: "#e0e0e0", fontWeight: 700 }} onClick={handleSave} disabled={saving}>
          {saving ? t('ingredientForm.saving') : t('ingredientForm.save')}
        </button>
      </div>

      {errors.length > 0 && (
        <div style={{ border: "1px solid #cc0000", background: "#fff0f0", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#cc0000" }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{ border: "1px solid #cc9900", background: "#fff9db", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#996600" }}>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
          {savedId && (
            <button style={{ ...btnStyle, marginTop: "6px" }} onClick={() => onSaved(savedId)}>
              {t('portal.continue')}
            </button>
          )}
        </div>
      )}

      {/* Paste from Excel */}
      <div style={sectionBox}>
        <div style={sectionTitle}>{t('ingredientForm.pasteFromExcel')}</div>
        <div style={{ padding: "8px" }}>
          <textarea
            value={pasteText}
            onChange={(e) => handlePaste(e.target.value)}
            rows={2}
            style={{ width: "100%", fontSize: "11px", fontFamily: mono, border: "1px solid #d0d0d0", padding: "4px", resize: "vertical", color: "#000" }}
            placeholder={t('ingredientForm.pastePlaceholder')}
          />
        </div>
      </div>

      <div style={{ maxWidth: "800px" }}>
        {/* Identity */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.identity')}</div>
          <TextInput label={t('ingredientForm.id')} value={form.id} onChange={set("id")} />
          <TextInput label={t('ingredientForm.commonName')} value={form.common_name} onChange={set("common_name")} />
          <TextInput label={t('ingredientForm.koreanName')} value={form.korean_name} onChange={set("korean_name")} />
          <TextInput label={t('ingredientForm.fdaName')} value={form.fda_name} onChange={set("fda_name")} />
          <TextInput label={t('ingredientForm.supplier')} value={form.supplier} onChange={set("supplier")} />
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.color')}</div>
            <ColorPickerField value={form.color} onChange={(v) => set("color")(v)} inputStyle={inputStyle} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.tags')}</div>
            <TagInput tags={form.tags} onChange={(t) => setForm((f) => ({ ...f, tags: t }))} placeholder={t('ingredientForm.typeAndPressEnter')} />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.proteinClass', { defaultValue: lang === "ko" ? "단백질 분류" : "Protein class" })}</div>
            <select
              value={form.protein_class}
              onChange={(e) => set("protein_class")(e.target.value)}
              style={{ ...inputStyle, fontFamily: "inherit" }}
            >
              <option value="">-</option>
              {PROTEIN_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {t(PROTEIN_CLASS_LABELS[c].key, { defaultValue: PROTEIN_CLASS_LABELS[c][lang] })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Nutrition */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.nutrition')}</div>
          <NumInput label={t('ingredientForm.proteinG100g')} value={form.protein_per_100g} onChange={set("protein_per_100g")} step="0.1" />
          <NumInput label={t('ingredientForm.fatLabel')} value={form.fat} onChange={set("fat")} step="0.1" />
          <NumInput label={t('ingredientForm.saturatedFat')} value={form.satfat} onChange={set("satfat")} step="0.1" />
          <NumInput label={t('ingredientForm.transFat')} value={form.transfat} onChange={set("transfat")} step="0.1" />
          <NumInput label={t('ingredientForm.cholesterol')} value={form.cholesterol} onChange={set("cholesterol")} step="0.1" />
          <NumInput label={t('ingredientForm.carbsLabel')} value={form.carbs} onChange={set("carbs")} step="0.1" />
          <NumInput label={t('ingredientForm.fiberLabel')} value={form.fiber} onChange={set("fiber")} step="0.1" />
          <NumInput label={t('ingredientForm.sugar')} value={form.sugar} onChange={set("sugar")} step="0.1" />
          <NumInput label={t('ingredientForm.sodium')} value={form.sodium} onChange={set("sodium")} step="0.1" />
          <NumInput label={t('ingredientForm.moistureLabel')} value={form.moisture} onChange={set("moisture")} step="0.1" />
          <NumInput label={t('ingredientForm.ashLabel')} value={form.ash} onChange={set("ash")} step="0.1" />
          <NumInput label={t('ingredientForm.costPerKg')} value={form.cost_per_kg} onChange={set("cost_per_kg")} step="0.01" />
        </div>

        {/* Fatty Acid Profile */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.fattyAcidProfile')}</div>
          <NumInput label={t('ingredientForm.ala')} value={form.fa_ala} onChange={set("fa_ala")} step="0.01" />
          <NumInput label={t('ingredientForm.la')} value={form.fa_la} onChange={set("fa_la")} step="0.01" />
          <NumInput label={t('ingredientForm.oleic')} value={form.fa_oleic} onChange={set("fa_oleic")} step="0.01" />
          <NumInput label={t('ingredientForm.palmitic')} value={form.fa_palmitic} onChange={set("fa_palmitic")} step="0.01" />
          <NumInput label={t('ingredientForm.stearic')} value={form.fa_stearic} onChange={set("fa_stearic")} step="0.01" />
        </div>

        {/* Amino Acids */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.aminoAcids')}</div>
          <NumInput label={t('ingredientForm.histidine')} value={form.aa_his} onChange={set("aa_his")} />
          <NumInput label={t('ingredientForm.isoleucine')} value={form.aa_ile} onChange={set("aa_ile")} />
          <NumInput label={t('ingredientForm.leucine')} value={form.aa_leu} onChange={set("aa_leu")} />
          <NumInput label={t('ingredientForm.lysine')} value={form.aa_lys} onChange={set("aa_lys")} />
          <NumInput label={t('ingredientForm.metCys')} value={form.aa_mc} onChange={set("aa_mc")} />
          <NumInput label={t('ingredientForm.pheTyr')} value={form.aa_pt} onChange={set("aa_pt")} />
          <NumInput label={t('ingredientForm.threonine')} value={form.aa_thr} onChange={set("aa_thr")} />
          <NumInput label={t('ingredientForm.tryptophan')} value={form.aa_trp} onChange={set("aa_trp")} />
          <NumInput label={t('ingredientForm.valine')} value={form.aa_val} onChange={set("aa_val")} />
        </div>

        {/* Fiber Fractions */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.fiberFractions')}</div>
          <NumInput label={t('ingredientForm.insolubleFiber')} value={form.fiber_insoluble} onChange={set("fiber_insoluble")} step="0.1" />
          <NumInput label={t('ingredientForm.solubleFiber')} value={form.fiber_soluble} onChange={set("fiber_soluble")} step="0.1" />
          <NumInput label={t('ingredientForm.resistantStarch')} value={form.resistant_starch} onChange={set("resistant_starch")} step="0.1" />
        </div>

        {/* Protein Digestibility */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.proteinDigestibility')}</div>
          <NumInput label={t('ingredientForm.digestibility01')} value={form.digestibility} onChange={set("digestibility")} step="0.01" />
          <TextInput label={t('ingredientForm.referenceProduct')} value={form.ref_digest_product} onChange={set("ref_digest_product")} />
          <NumInput label={t('ingredientForm.referenceValue')} value={form.ref_digest_value} onChange={set("ref_digest_value")} step="0.01" />
          <TextInput label={t('ingredientForm.referenceCitation')} value={form.ref_digest_source} onChange={set("ref_digest_source")} />
        </div>

        {/* Micronutrients */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.micronutrients')}</div>
          <NumInput label={t('ingredientForm.vitaminD')} value={form.vitamin_d} onChange={set("vitamin_d")} />
          <NumInput label={t('ingredientForm.calcium')} value={form.calcium} onChange={set("calcium")} />
          <NumInput label={t('ingredientForm.iron')} value={form.iron} onChange={set("iron")} />
          <NumInput label={t('ingredientForm.potassium')} value={form.potassium} onChange={set("potassium")} />
        </div>

        {/* Specs */}
        <div style={sectionBox}>
          <div style={sectionTitle}>{t('ingredientForm.specs')}</div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.subIngredients')}</div>
            <textarea
              value={form.sub_ingredients}
              onChange={(e) => set("sub_ingredients")(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>
          <div style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{t('ingredientForm.notesSpecRef')}</div>
            <textarea
              value={form.notes_spec_ref}
              onChange={(e) => set("notes_spec_ref")(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>
          <div style={fieldRowStyle}>
            <div style={{ ...fieldLabelStyle, fontWeight: 700, color: "#4a4a8a" }}>{t('ingredientForm.primaryDataSource')}</div>
            <input
              type="text"
              value={form.data_source_default}
              onChange={(e) => set("data_source_default")(e.target.value)}
              placeholder={t('ingredientForm.dataSourcePlaceholder')}
              style={inputStyle}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
